import { useState, useCallback, useMemo } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Field, stopAsyncValidation } from "redux-form";
import { compose } from "recompose";
import { connect } from "react-redux";
import {
  Card,
  Form,
  FormControl,
  withPrompt,
  withOverlay,
} from "@dpdgroupuk/mydpd-ui";
import classNames from "classnames";

import {
  APPLY_ALL_ACCOUNTS,
  LOGO,
  SIGNATURE,
  POSITION_IN_COMPANY,
  NAME,
  ACCOUNT,
} from "../../../../../constants/strings";
import { PaperlessActions } from "../../../../../store/paperless";
import { M, ALLOWED_IMAGE_FILE_EXTENSIONS, F } from "../../../../../constants";
import * as S from "../../../../../constants/strings";
import * as PaperlessSettingsFields from "../../../../../constants/forms/paperlessSettings";
import * as PaperlessSelectors from "../redux/selectors";
import * as ImageApi from "../../../../../apis/image";
import styles from "./PaperlessSettings.module.scss";

const TermsAndConditionsLabelCheckbox = () => (
  <span className={styles.termsAndConditions}>
    I agree to Paperless Trade&nbsp;
    <a
      href="https://www.dpd.co.uk/dpd-online-terms-and-conditions.jsp"
      target="_blank"
      rel="noreferrer"
    >
      Terms &amp; Conditions
    </a>
  </span>
);

const PaperlessSettings = ({
  accounts,
  values,
  onSelectAllAccounts,
  onAgreeTermsChange,
  onAccountChange,
  onFileChange,
}) => {
  const [signatureFileUrl, setSignatureFile] = useState(null);
  const [logoFileUrl, setLogoFile] = useState(null);

  const getImageUrl = useCallback((files, imageType) => {
    if (files) {
      const file = files[0];

      if (!file.lastModified) {
        return ImageApi.getImageUrl(file.originName, imageType);
      } else {
        return URL.createObjectURL(file);
      }
    }
    return null;
  }, []);

  useMemo(() => {
    setLogoFile(
      getImageUrl(values[PaperlessSettingsFields.LOGO], "customs_logo_image")
    );
  }, [values[PaperlessSettingsFields.LOGO], setLogoFile, getImageUrl]);

  useMemo(() => {
    setSignatureFile(
      getImageUrl(
        values[PaperlessSettingsFields.SIGNATURE],
        "customs_signature_image"
      )
    );
  }, [
    values[PaperlessSettingsFields.SIGNATURE],
    setSignatureFile,
    getImageUrl,
  ]);

  const accountsValues = useMemo(() => {
    if (accounts > 1) {
      return [
        { label: S.PLEASE_SELECT_$(S.ACCOUNT), value: null },
        ...accounts,
      ];
    }
    return accounts;
  }, [accounts]);

  return (
    <Card>
      <Card.Header>{S.PAPERLESS_SETTINGS}</Card.Header>
      {!accounts.length && (
        <Container className="pl-0">
          <Row>
            <Col>Currently there is no available accounts</Col>
          </Row>
        </Container>
      )}
      {!!accounts.length && (
        <Container className="pl-0">
          <Row>
            <Col sm={7}>
              <Row className="align-items-start h-100 pl-0">
                <Col sm={12}>
                  Opt in to Paperless Trade so you can ship to countries around
                  the world without the need to print and attach customs
                  documents.
                </Col>
                <Col sm={12}>
                  Please provide the name and position in company to appear on
                  your digital customs invoices and upload the accompanying
                  signature and logo.
                </Col>
                <Col sm={12}>
                  <Field
                    component={Form.Check}
                    type="checkbox"
                    label={<TermsAndConditionsLabelCheckbox />}
                    className={"mb-0"}
                    name={PaperlessSettingsFields.AGREE}
                    id={PaperlessSettingsFields.AGREE}
                    maxLength={25}
                    onChange={onAgreeTermsChange}
                  />
                </Col>
              </Row>
            </Col>
            <Col
              sm={5}
              className={classNames(
                styles.importantInformation,
                "mb-3",
                "pl-3",
                "pt-3"
              )}
            >
              <Row>
                <Col sm={12} className="font-weight-bold mb-3">
                  Important Information
                </Col>
                <Col sm={12} className="mb-3">
                  Tick the box to agree to the Paperless Trade Terms &
                  Conditions - if at any time you want to stop Paperless Trade,
                  simply untick the box and it will cease the next day.
                </Col>
                <Col sm={12} className="mb-2">
                  Any changes you make to Paperless Trade settings will take
                  effect tomorrow.
                </Col>
              </Row>
            </Col>
          </Row>
          {values.agree && (
            <>
              <Row>
                <Col sm={4}>
                  <Field
                    component={FormControl.Dropdown}
                    label={ACCOUNT}
                    name={PaperlessSettingsFields.ACCOUNT}
                    values={accountsValues}
                    textTransform={M.UPPERCASE}
                    onChange={onAccountChange}
                    placeholder={M.PLEASE_SELECT_$(M.ACCOUNT, {
                      article: "an",
                    })}
                    disabled={values.allAccounts}
                  />
                </Col>
                <Col sm={4}>
                  <Field
                    component={FormControl.Input}
                    label={NAME}
                    name={PaperlessSettingsFields.NAME}
                    textTransform={M.UPPERCASE}
                    required
                    maxLength={50}
                    helperText={M.MAX_$_CHARACTERS(50)}
                    disabled={!values.account && !values.allAccounts}
                  />
                </Col>
                <Col sm={4}>
                  <Field
                    component={FormControl.Input}
                    label={POSITION_IN_COMPANY}
                    name={PaperlessSettingsFields.TITLE}
                    textTransform={M.UPPERCASE}
                    required
                    maxLength={50}
                    helperText={M.MAX_$_CHARACTERS(50)}
                    disabled={!values.account && !values.allAccounts}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={6}>
                  <Field
                    component={FormControl.Input}
                    type="file"
                    label={SIGNATURE}
                    name={PaperlessSettingsFields.SIGNATURE}
                    accept={ALLOWED_IMAGE_FILE_EXTENSIONS}
                    onBlur={event => event.preventDefault()} // used to avoid setting empty object to store when prompt window was opened
                    onChange={onFileChange}
                    disabled={!values.account && !values.allAccounts}
                  />
                </Col>
                <Col sm={6}>
                  <Field
                    component={FormControl.Input}
                    type="file"
                    label={LOGO}
                    name={PaperlessSettingsFields.LOGO}
                    accept={ALLOWED_IMAGE_FILE_EXTENSIONS}
                    onBlur={event => event.preventDefault()} // used to avoid setting empty object to store when prompt window was opened
                    onChange={onFileChange}
                    disabled={!values.account && !values.allAccounts}
                  />
                </Col>
              </Row>
              {(signatureFileUrl || logoFileUrl) && (
                <Row>
                  <Col sm={6}>
                    <Form.Group>
                      {signatureFileUrl && (
                        <img
                          src={signatureFileUrl}
                          width="100px"
                          alt="Signature image"
                        />
                      )}
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      {logoFileUrl && (
                        <img src={logoFileUrl} width="100px" alt="Logo image" />
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <Row>
                <Col sm={4}>
                  <Field
                    component={Form.Check}
                    type="checkbox"
                    label={APPLY_ALL_ACCOUNTS}
                    name={PaperlessSettingsFields.ALL_ACCOUNTS}
                    id={PaperlessSettingsFields.ALL_ACCOUNTS}
                    onChange={onSelectAllAccounts}
                  />
                </Col>
              </Row>
            </>
          )}
        </Container>
      )}
    </Card>
  );
};

const mapDispatchToProps = (dispatch, { prompt, overlay }) => ({
  onAgreeTermsChange: event => {
    if (!event.target.checked) {
      event.preventDefault();
      prompt.showConfirmation({
        header: "Warning",
        message: `Are you sure you want to opt out of paperless trading ?`,
        onConfirm: async () => {
          try {
            overlay.show();
            await dispatch(PaperlessActions.disagreeTermsAndConditions());
          } finally {
            overlay.hide();
          }
        },
      });
    } else {
      dispatch(PaperlessActions.agreeTermsAndConditions());
    }
  },
  onAccountChange: event =>
    dispatch(PaperlessActions.changeAccount(event.target.value)),
  onSelectAllAccounts: event =>
    event.target.checked && dispatch(PaperlessActions.applyAllAccounts()),
  onFileChange: async (files, _, __, fieldName) => {
    await dispatch(
      PaperlessActions.validateImagePaperlessForm(fieldName, files)
    );
  },
});

const mapStateToProps = state => ({
  accounts: PaperlessSelectors.getFormAccounts(state),
  values: PaperlessSelectors.getPaperlessFormValues(state),
});

export default compose(
  withPrompt,
  withOverlay,
  connect(mapStateToProps, mapDispatchToProps)
)(PaperlessSettings);
